import { useEffect, useState } from 'react';
import {
  useActionHandlers,
  useDialogState,
  useLoadFromLocalStorage,
  useSearchFieldStates,
  useTableData
} from './hooks';
import { type LedgerDocumentEntryState, resolveLedgerDocumentEntry } from './utils/document-entry';
import { ActionBar, EditPrintTemplateDialog, InitialSearchDialog } from './components';
import { SoChiTietCongNoProvider, useSoChiTietCongNoContext } from './context';
import { initialValues, type SearchFormValues } from './schema';
import { ErpDataTableV2 } from '@/components/custom/erp';
import DocumentEntry from './components/DocumentEntry';
import { useDynamicSearch, useReport } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { getColumns } from './utils';

function DebtBalanceReportPageContent() {
  const {
    initialSearchDialogOpen,
    editPrintTemplateDialogOpen,
    showTable,
    setShowTable,
    searchParams,
    setSearchParams,
    handleInitialSearchClose,
    handleInitialSearch: handleInitialSearchHook,
    handleSearchClick,
    handleEditPrintTemplateClick,
    handleClosePrintTemplateDialog,
    handleSavePrintTemplate
  } = useDialogState();

  const { getReport, isLoading, data, currentPage, handlePageChange, totalItems } = useReport<
    MuaHangRecord,
    SearchFormValues
  >({
    endpoint: QUERY_KEYS.SO_CHI_TIET_CONG_NO_THEO_KHACH_HANG,
    pageSize: 50
  });

  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch,
    handleRefreshClick: handleDynamicRefreshClick,
    isSearching
  } = useDynamicSearch({
    onSearch: getReport,
    params: searchParams,
    onInitialSearch: handleInitialSearchHook,
    getColumns,
    endpoint: QUERY_KEYS.SO_CHI_TIET_CONG_NO_THEO_KHACH_HANG,
    pageSize: 50
  });

  const [lastFormValues, setLastFormValues] = useState<SearchFormValues>(initialValues);
  const [documentEntry, setDocumentEntry] = useState<LedgerDocumentEntryState | null>(null);
  const handleDocumentFormClick = (row: MuaHangRecord) => {
    setDocumentEntry(resolveLedgerDocumentEntry(row));
  };

  const { tables, handleRowClick } = useTableData(searchParams, handleDocumentFormClick);
  const { handleRefreshClick, handleFixedColumnsClick } = useActionHandlers();
  const { setQueryParams, isExporting } = useSoChiTietCongNoContext();
  const searchFieldStates = useSearchFieldStates();

  useLoadFromLocalStorage({
    searchFieldStates,
    setSearchParams,
    setQueryParams,
    setShowTable,
    handleInitialSearchClose,
    setFormValues: setLastFormValues
  });

  useEffect(() => {
    if (searchParams && !hasFilterParams && showTable) {
      getReport(searchParams);
    }
  }, [searchParams, hasFilterParams, showTable, getReport]);

  const handleSearchWithData = async (payload: SearchFormValues, rawValues: SearchFormValues) => {
    setQueryParams(payload);
    setLastFormValues(rawValues);
    handleInitialSearch(payload);
  };

  const handleRefreshWithData = async () => {
    if (hasFilterParams) {
      handleDynamicRefreshClick();
    } else {
      await getReport(searchParams!);
    }
    handleRefreshClick();
  };

  const resolvedRows = hasFilterParams ? searchState.searchResults : data;
  const resolvedCurrentPage = hasFilterParams ? searchState.currentPage : currentPage;
  const resolvedTotalItems = hasFilterParams ? searchState.totalCount : totalItems;
  const resolvedHandlePageChange = hasFilterParams ? handleSearchPageChange : handlePageChange;

  return (
    <div className='flex h-screen w-full flex-col overflow-hidden'>
      <InitialSearchDialog
        open={initialSearchDialogOpen}
        onClose={handleInitialSearchClose}
        onSearch={handleSearchWithData}
        initialData={lastFormValues}
        searchFieldStates={searchFieldStates}
      />

      <EditPrintTemplateDialog
        open={editPrintTemplateDialogOpen}
        onClose={handleClosePrintTemplateDialog}
        onSave={handleSavePrintTemplate}
      />

      {documentEntry && <DocumentEntry document={documentEntry} onClose={() => setDocumentEntry(null)} />}

      {showTable && !documentEntry && (
        <>
          <ActionBar
            onSearchClick={handleSearchClick}
            onRefreshClick={handleRefreshWithData}
            onFixedColumnsClick={handleFixedColumnsClick}
            onEditPrintTemplateClick={handleEditPrintTemplateClick}
            className='border-b border-gray-200'
            searchFieldStates={searchFieldStates}
            searchParams={searchParams}
          />

          <div className='flex-1 overflow-hidden'>
            {!isLoading && (
              <ErpDataTableV2
                tables={[
                  {
                    ...tables[0],
                    rows: resolvedRows.map((item, index) => {
                      const isSummaryRow = ['Đầu kỳ', 'Tổng phát sinh', 'Cuối kỳ'].includes(
                        item.dien_giai?.trim() || ''
                      );
                      return {
                        ...item,
                        id: item.id || item.xid || `row-${index}`,
                        isSummaryRow
                      };
                    })
                  }
                ]}
                onRowClick={handleRowClick}
                totalItems={resolvedTotalItems}
                pageSize={50}
                currentPage={resolvedCurrentPage}
                onPageChange={resolvedHandlePageChange}
                onSearchChange={handleSearchChange}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function DebtBalanceReportPage() {
  return (
    <SoChiTietCongNoProvider>
      <DebtBalanceReportPageContent />
    </SoChiTietCongNoProvider>
  );
}
